import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function useDesktopSearch({ destinations, setMapCenter, setSearchZoom, setSelectedDestination }) {
  const [desktopSearchQuery, setDesktopSearchQuery] = useState("");
  const [desktopSearchResults, setDesktopSearchResults] = useState(null);
  const [desktopSearchLoading, setDesktopSearchLoading] = useState(false);
  const [desktopSearchOpen, setDesktopSearchOpen] = useState(false);
  const [desktopSelectedIndex, setDesktopSelectedIndex] = useState(-1);
  const desktopSearchRef = useRef(null);
  const desktopDropdownRef = useRef(null);

  const fetchDesktopSearchResults = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) { setDesktopSearchResults(null); return; }
    setDesktopSearchLoading(true);
    try {
      const response = await axios.get(`${API}/search`, { params: { q: searchQuery, limit: 8, include_geocode: true } });
      setDesktopSearchResults(response.data);
      setDesktopSearchOpen(true);
      setDesktopSelectedIndex(-1);
    } catch (error) { setDesktopSearchResults(null); }
    finally { setDesktopSearchLoading(false); }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (desktopDropdownRef.current && !desktopDropdownRef.current.contains(event.target) &&
          desktopSearchRef.current && !desktopSearchRef.current.contains(event.target)) {
        setDesktopSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDesktopSelectLocation = useCallback((location) => {
    setMapCenter([location.latitude, location.longitude]);
    setSearchZoom(location.type === "location" ? 10 : 12);
    if (location.type === "destination") {
      const dest = destinations.find(d => d.id === location.data.id);
      if (dest) setSelectedDestination(dest);
    }
    setDesktopSearchQuery("");
    setDesktopSearchResults(null);
    setDesktopSearchOpen(false);
    setTimeout(() => { setMapCenter(null); setSearchZoom(null); }, 2000);
  }, [destinations, setMapCenter, setSearchZoom, setSelectedDestination]);

  const handleDesktopKeyDown = (e) => {
    if (!desktopSearchOpen || !desktopSearchResults) return;
    const allResults = [
      ...(desktopSearchResults.destinations || []).map(d => ({ ...d, type: 'destination' })),
      ...(desktopSearchResults.access_points || []).map(a => ({ ...a, type: 'access_point' })),
      ...(desktopSearchResults.locations || []).map(l => ({ ...l, type: 'location' }))
    ];
    if (e.key === 'ArrowDown') { e.preventDefault(); setDesktopSelectedIndex(prev => Math.min(prev + 1, allResults.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setDesktopSelectedIndex(prev => Math.max(prev - 1, -1)); }
    else if (e.key === 'Enter' && desktopSelectedIndex >= 0) {
      e.preventDefault();
      const selected = allResults[desktopSelectedIndex];
      if (selected) handleDesktopSelectLocation({ latitude: selected.latitude, longitude: selected.longitude, type: selected.type, data: selected });
    }
    else if (e.key === 'Escape') setDesktopSearchOpen(false);
  };

  const clearDesktopSearch = () => {
    setDesktopSearchQuery("");
    setDesktopSearchResults(null);
    setDesktopSearchOpen(false);
    desktopSearchRef.current?.focus();
  };

  return {
    desktopSearchQuery, setDesktopSearchQuery,
    desktopSearchResults, setDesktopSearchResults,
    desktopSearchLoading, desktopSearchOpen, setDesktopSearchOpen,
    desktopSelectedIndex, desktopSearchRef, desktopDropdownRef,
    fetchDesktopSearchResults, handleDesktopSelectLocation,
    handleDesktopKeyDown, clearDesktopSearch,
  };
}
